/**
 * /wiki/{slug}-{8charTail} — Notion-style wiki doc URL (Phase 5 Wave B).
 *
 * The slug is decorative; the trailing 8 lowercase hex chars are the
 * BE resolver key (matched against the first 8 hex chars of the node
 * UUID with dashes stripped). Stable across rename + move because the
 * tail is derived from `id` — only the slug changes when a doc is
 * renamed.
 *
 * Resolution:
 *   1. Extract the tail with `parseDocPathTail`. Malformed shape →
 *      route renders a 404 surface (no BE round-trip).
 *   2. Call `GET /api/wiki/docs/by-tail/:tail`. 200 → render
 *      `<DocPage docIdOverride>`. 404 → 404 surface. 409 (rare
 *      ~1.2% / 100k docs per tenant) → the loaded tree picks the one
 *      node whose full slug matches; only when it can't does the
 *      collision message render.
 *
 * Folders ride the same resolver — the BE returns folder nodes too,
 * matching the legacy `/wiki/d/{folderId}` behavior. For folders the
 * route redirects to the legacy `/wiki/d/:id` form since
 * `FolderDetailPage` reads its params directly via TanStack (a
 * symmetric prop-refactor is out of scope for this wave).
 */

import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { ApiError, apiClient } from '@/api/client'
import type { WikiGetDocResponse } from '@/api/types-bridge'
import { DocAccessPanel } from '@/features/wiki/components/DocAccessPanel'
import { DocPage } from '@/features/wiki/components/DocPage'
import { FileNodePage } from '@/features/wiki/components/FileNodePage'
import { WikiDocSkeleton } from '@/features/wiki/components/WikiDocSkeleton'
import { wikiDocByTailQueryKey } from '@/features/wiki/hooks/query-keys'
import { useWikiTree } from '@/features/wiki/hooks/use-wiki-tree'
import { parseDocPathTail, resolveTailCollision } from '@/features/wiki/lib/slug'

interface DocSearch {
  focusTitle?: '1'
}

export const Route = createFileRoute('/wiki/$slugWithTail')({
  component: PrettyDocRoute,
  validateSearch: (search: Record<string, unknown>): DocSearch => ({
    // Mirrors the legacy `/wiki/d/$docId/` route's validator so the
    // `focusTitle=1` deep link survives the legacy → pretty redirect.
    // Anything else collapses to undefined so a malformed inbound link
    // doesn't trap the title input in permanently-focused state.
    focusTitle: search.focusTitle === '1' ? '1' : undefined,
  }),
})

function PrettyDocRoute() {
  const { slugWithTail } = Route.useParams() as { slugWithTail: string }
  const tail = parseDocPathTail(slugWithTail)
  const { t } = useTranslation('wiki')
  const navigate = useNavigate()
  const { nodesById } = useWikiTree()
  const backToWiki = useCallback(() => {
    void navigate({ to: '/wiki' })
  }, [navigate])

  // Cache key the BE-resolved doc by tail so a tab-switch + back doesn't
  // re-resolve. `staleTime: 30s` matches `useWikiDoc`. The query is
  // disabled when the tail format is invalid so the malformed path
  // surfaces a 404 without a wasted BE round-trip.
  const query = useQuery<WikiGetDocResponse>({
    queryKey: wikiDocByTailQueryKey(tail),
    queryFn: async () => {
      const res = await apiClient<{ data: WikiGetDocResponse }>(`/api/wiki/docs/by-tail/${tail}`)
      return res.data
    },
    enabled: tail !== null,
    staleTime: 30_000,
    retry: false,
  })

  // Malformed shape — render the 404 surface directly. Covers `/wiki/abcdef`
  // (no trailing 8 hex), `/wiki/foo-BAR12345` (uppercase), etc.
  // The not-found surface renders IN PLACE — the URL stays put so the
  // user can see what they pasted, and the copy never distinguishes
  // "absent" from "restricted" (the BE answers one 404 for both).
  if (tail === null) {
    return (
      <DocAccessPanel
        title={t('docPage.accessLost.notFoundTitle')}
        body={t('urlResolver.notFound')}
        onBackToWiki={backToWiki}
      />
    )
  }

  if (query.isLoading) {
    return <WikiDocSkeleton />
  }

  if (query.isError) {
    const err = query.error
    // `ApiError.body` carries the BE wire envelope `{ error: { code,
    // message } }`. The status check pairs with the body parse so a
    // 409 without a code (network-level edge case) still falls
    // through to the generic not-found surface.
    const code =
      err instanceof ApiError
        ? ((err.body as { error?: { code?: string } } | null | undefined)?.error?.code ?? null)
        : null
    if (code === 'wiki_tail_collision' || (err instanceof ApiError && err.status === 409)) {
      // Two nodes share this tail, but the slug usually still tells them
      // apart — resolve against the loaded tree and open the one this URL
      // names. Rendering the page here (rather than bouncing to the
      // legacy `/wiki/d/:id` route) matters: that route redirects every
      // doc straight back to its pretty URL, which would loop.
      const resolved = resolveTailCollision(slugWithTail, nodesById.values())
      if (resolved?.kind === 'file') return <FileNodePage nodeId={resolved.id} />
      if (resolved) return <DocPage docIdOverride={resolved.id} />
      return (
        <div className="flex flex-1 items-center justify-center p-8 text-center">
          <p className="text-sm text-fg-muted">{t('urlResolver.collisionMessage')}</p>
        </div>
      )
    }
    return (
      <DocAccessPanel
        title={t('docPage.accessLost.notFoundTitle')}
        body={t('urlResolver.notFound')}
        onBackToWiki={backToWiki}
      />
    )
  }

  const doc = query.data?.doc
  if (!doc) {
    return <WikiDocSkeleton />
  }

  // Folder branch — mirrors the legacy `/wiki/d/$docId/index.tsx`. Pretty
  // URL hitting a folder normalizes to the legacy `/wiki/d/:id` form
  // since `FolderDetailPage` reads its params directly via TanStack and
  // a symmetric prop refactor is out of scope for this wave.
  if (doc.kind === 'folder') {
    return <FolderRedirect docId={doc.id} />
  }

  // File nodes render on the same surface as docs (their own page), NOT a
  // modal. The by-tail resolver returns file nodes now (BE getDoc was
  // generalized to serve kind='file').
  if (doc.kind === 'file') {
    return <FileNodePage nodeId={doc.id} />
  }

  return <DocPage docIdOverride={doc.id} />
}

/**
 * One-shot legacy-route redirect for folders. `replace: true` keeps the
 * back button from getting trapped on the pretty URL the user just
 * came from.
 */
function FolderRedirect({ docId }: { docId: string }) {
  const navigate = useNavigate()
  useEffect(() => {
    void navigate({ to: '/wiki/d/$docId', params: { docId }, replace: true })
  }, [docId, navigate])
  return <WikiDocSkeleton />
}
